import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, MapPin, Users } from 'lucide-react';
import { BrutalButton } from '@/components/ui/BrutalButton';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { useTranslation } from 'react-i18next';

interface EventCardProps {
  data: {
    id: string;
    title: string;
    slug: string;
    start_datetime: string;
    location_city?: string;
    cover_image_url?: string;
    current_participants?: number;
    max_participants?: number;
    event_type?: string;
    event_format?: string;
    price?: number;
    currency?: string;
  };
}

export const EventCard = ({ data }: EventCardProps) => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [imageLoaded, setImageLoaded] = useState(false);

  const handleClick = () => {
    navigate(`/event/${data.slug}`);
  };

  const getLocationText = () => {
    if (data.event_format === 'online') {
      return t('events.online');
    }
    return data.location_city || t('events.location');
  };

  return (
    <div
      className="border-[3px] border-dark bg-card shadow-[6px_6px_0px_0px] shadow-dark hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-[4px_4px_0px_0px] transition-all cursor-pointer overflow-hidden h-full flex flex-col"
      onClick={handleClick}
    >
      {/* Image Section with Skeleton */}
      <div className="relative w-full aspect-[3/1] border-b-[3px] border-dark">
        {data.cover_image_url ? (
          <>
            {/* Skeleton while loading */}
            {!imageLoaded && (
              <Skeleton className="absolute inset-0 w-full h-full" />
            )}
            <img
              src={data.cover_image_url}
              alt={data.title}
              className={`w-full h-full object-cover transition-opacity duration-300 ${
                imageLoaded ? 'opacity-100' : 'opacity-0'
              }`}
              loading="lazy"
              onLoad={() => setImageLoaded(true)}
            />
          </>
        ) : (
          <div className="w-full h-full bg-muted flex items-center justify-center">
            <Calendar className="w-16 h-16 text-muted-foreground" />
          </div>
        )}
      </div>
      
      <div className="p-6 flex-1 flex flex-col">
        {/* Content wrapper */}
        <div className="space-y-4 flex-1 pb-4">
          <div className="flex items-start justify-between gap-2">
            <h3 className="font-heading font-bold text-lg line-clamp-2">
              {data.title}
            </h3>
            <Badge className={
              data.event_type === 'free' 
                ? "bg-emerald-500 text-white text-[10px] font-bold uppercase px-1.5 py-0.5 border-0" 
                : "bg-blue-500 text-white text-[10px] font-bold uppercase px-1.5 py-0.5 border-0"
            }>
              {data.event_type === 'free' ? t('events.free') : `${data.price || ''} ${data.currency || '€'}`}
            </Badge>
          </div>

          <div className="text-sm space-y-2">
            <div className="flex items-center gap-2 text-muted-foreground">
              <Calendar className="w-4 h-4 flex-shrink-0" />
              <span>
                {new Date(data.start_datetime).toLocaleDateString(i18n.language, {
                  day: 'numeric',
                  month: 'short',
                  year: 'numeric'
                })}
              </span>
            </div>

            <div className="flex items-center gap-2 text-muted-foreground">
              <MapPin className="w-4 h-4 flex-shrink-0" />
              <span className="truncate">{getLocationText()}</span>
            </div>

            {data.max_participants && (
              <div className="flex items-center gap-2 text-muted-foreground">
                <Users className="w-4 h-4 flex-shrink-0" />
                <span>
                  {data.current_participants || 0}/{data.max_participants} {t("events.participants")}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Button always at bottom */}
        <BrutalButton 
          size="sm" 
          className="w-full mt-auto"
        >
          {t('events.viewDetails')}
        </BrutalButton>
      </div>
    </div>
  );
};
